import { Box, Flex, IconButton, Image, Stack, Text } from '@chakra-ui/react';
import { OcrItemDto } from '@diamond/shared/types';
import DeleteOutline from '@mui/icons-material/DeleteOutline';

import { AnalyzingChip } from './AnalyzingChip';
import { ManualFallbackFields } from './ManualFallbackFields';

const DUPLICATE_PAYMENT_PROOF_WARNING =
  'Bukti pembayaran sudah pernah terpakai. Silakan upload ulang apabila terdapat kesalahan.';

interface OcrPreviewCardProps {
  item: OcrItemDto;
  state: 'analyzing' | 'success' | 'manual_fallback' | 'duplicate' | 'blocked';
  fieldIndex: number;
  onDelete: () => void;
  isDeleting?: boolean;
  bankFieldLabel?: string;
  layout?: 'stack' | 'row';
}

export const OcrPreviewCard = ({
  item,
  state,
  fieldIndex,
  onDelete,
  isDeleting = false,
  bankFieldLabel,
  layout = 'stack',
}: OcrPreviewCardProps) => {
  function renderStateContent() {
    if (state === 'analyzing') {
      return <AnalyzingChip />;
    }

    if (state === 'success') {
      return (
        <ManualFallbackFields
          bankFieldLabel={bankFieldLabel}
          fieldIndex={fieldIndex}
          layout={layout}
          showWarning={false}
        />
      );
    }

    if (state === 'manual_fallback') {
      return (
        <ManualFallbackFields
          bankFieldLabel={bankFieldLabel}
          fieldIndex={fieldIndex}
          layout={layout}
          warningMessage={item.display_error?.message}
        />
      );
    }

    if (state === 'duplicate') {
      return (
        <ManualFallbackFields
          bankFieldLabel={bankFieldLabel}
          fieldIndex={fieldIndex}
          layout={layout}
          warningMessage={DUPLICATE_PAYMENT_PROOF_WARNING}
        />
      );
    }

    if (state === 'blocked') {
      return (
        <Text fontSize="12px" color="red.500" fontWeight={500}>
          {item.display_error?.message ?? 'Bukti transfer tidak dapat diproses'}
        </Text>
      );
    }

    return null;
  }

  return (
    <Box
      bg="#F1F8FF"
      borderRadius="8px"
      minH={layout === 'row' ? undefined : '274px'}
      p="16px"
      w="100%"
      maxW={layout === 'row' ? '100%' : { base: '100%', md: '330px' }}
    >
      <Flex
        align="flex-start"
        direction={layout === 'row' ? { base: 'column', md: 'row' } : 'row'}
        gap={layout === 'row' ? '16px' : '12px'}
        w="100%"
      >
        <Box flexShrink={0}>
          <Image
            src={item.preview_url}
            alt={item.file_name}
            w="120px"
            h="120px"
            borderRadius="4px"
            objectFit="cover"
            fallback={
              <Box bg="gray.100" w="120px" h="120px" borderRadius="4px" />
            }
          />
        </Box>

        <Stack flex={1} maxW="100%" minW={0} spacing={3} w="100%">
          <Flex justify="space-between" align="flex-start" gap="8px">
            <Text
              fontSize="14px"
              fontWeight={400}
              color="#373f41"
              lineHeight="20px"
              noOfLines={1}
              wordBreak="normal"
              flex={1}
            >
              {item.file_name}
            </Text>
            <IconButton
              aria-label="Hapus bukti transfer"
              icon={<DeleteOutline sx={{ fontSize: 20 }} />}
              variant="ghost"
              color="#A0A0A0"
              bg="transparent"
              minW="20px"
              w="20px"
              h="20px"
              p={0}
              flexShrink={0}
              _active={{ bgColor: 'white' }}
              _hover={{ bgColor: 'gray.33' }}
              onClick={onDelete}
              isLoading={isDeleting}
            />
          </Flex>

          {renderStateContent()}
        </Stack>
      </Flex>
    </Box>
  );
};
